import { Download } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import BeihilferelevanzPostVersandInfo, {
	BeihilferelevanzNachdemDownloadInformation,
} from './BeihilferelevanzPostVersandInfo';
import { BEIHILFERELEVANZ_PATH } from 'src/lib/constants/componentsPath';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, FORM_FIELDS_TYPES, V_PARTNER_KEINE_ANGABEN_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { NonRequired } from 'src/lib/validation/ValidationTypes'; //Todo: Add "ForceTrue" once backend persists components
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, isTrue } from 'src/utils/helper';

// import schemas
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import AntragSchema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

// Selection option constants
const SELECTION_UMLAGE = 'UmsetzungEinerTransparentenUmlage';
const SELECTION_MARKTPREISEN = 'AbrechnungDesAngebotsZuMarktpreisen';

const BeihilferelevanzErklaerungenGlobal: FC<{
	unternehmenId: number;
	unternehmenName: string;
	positionId?: string;
	hasComment?: boolean;
}> = (props) => {
	const { unternehmenId, positionId, unternehmenName, hasComment } = props;

	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleUpdateErklaerungDaten = (key: string, value: any, beihilfeIndex: number) => {
		let appCopy = cloneDeep(application);
		set(appCopy, key, value);
		const unternehmenPath = addIndexesToPath(
			P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Unternehmen.path,
			[`${beihilfeIndex}`],
			['Beihilferelevanz_Selbsterklaerung'],
		);
		if (Number(get(appCopy, `${unternehmenPath}.id`, null)) > 0) {
			// DO Nothing, since the unternehmen ID already exist
		} else {
			set(appCopy, unternehmenPath, unternehmenId);
		}

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	// Handle the 3-way selection
	// (enumeration `Erklaerung_Beihilferelevante_Angebote` + boolean `Erklaerung_Beihilfebegriff`).
	const handleUpdateAuswahl = (key: string, value: any, beihilfeIndex: number) => {
		let appCopy = cloneDeep(application);
		set(appCopy, key, value);

		const angebotePath = addIndexesToPath(
			P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Erklaerung_Beihilferelevante_Angebote.path,
			[`${beihilfeIndex}`],
			['Beihilferelevanz_Selbsterklaerung'],
		);
		const beihilfebegriffPath = addIndexesToPath(
			P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Erklaerung_Beihilfebegriff.path,
			[`${beihilfeIndex}`],
			['Beihilferelevanz_Selbsterklaerung'],
		);

		// The two fields represent one single 3-way choice -> keep them mutually exclusive.
		if (key === angebotePath && value) {
			set(appCopy, beihilfebegriffPath, false);
		} else if (key === beihilfebegriffPath && isTrue(value)) {
			set(appCopy, angebotePath, null);
		}

		const unternehmenPath = addIndexesToPath(
			P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Unternehmen.path,
			[`${beihilfeIndex}`],
			['Beihilferelevanz_Selbsterklaerung'],
		);
		if (Number(get(appCopy, `${unternehmenPath}.id`, null)) > 0) {
			// DO Nothing, since the unternehmen ID already exist
		} else {
			set(appCopy, unternehmenPath, unternehmenId);
		}

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	const handleGenerateSelbstErklaerung = async () => {
		// TODO: Implement Beihilferelevanz PDF download via template service
	};

	if (unternehmenId) {
		return (
			<>
				{get(application, P.Beihilferelevanz_Selbsterklaerung.path, []).map((ele: any, i: number) => {
					if (
						(Number(get(ele, `${BEIHILFERELEVANZ_PATH.Unternehmen}.id`, null)) === unternehmenId &&
							!get(ele, BEIHILFERELEVANZ_PATH.PositionID, null)) ||
						get(ele, BEIHILFERELEVANZ_PATH.PositionID, '') === positionId
					) {
						const angeboteValue = get(ele, BEIHILFERELEVANZ_PATH.Erklaerung_Beihilferelevante_Angebote, '');
						const beihilfebegriffValue = get(ele, BEIHILFERELEVANZ_PATH.Erklaerung_Beihilfebegriff, false);

						return (
							<React.Fragment key={i}>
								<PruefAssistenz
									statusPath={addIndexesToPath(
										P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Pruefung_Pruefstatus.path,
										[`${i}`],
										['Beihilferelevanz_Selbsterklaerung'],
									)}
									hinweisPath={addIndexesToPath(
										P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Pruefung_Hinweis_Fuer_Den_Antragsteller.path,
										[`${i}`],
										['Beihilferelevanz_Selbsterklaerung'],
									)}
									ueberarbeitetAmPath={addIndexesToPath(
										P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Pruefung_Ueberarbeitet_Am.path,
										[`${i}`],
										['Beihilferelevanz_Selbsterklaerung'],
									)}
									erledigtPath={addIndexesToPath(
										P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Pruefung_Erledigt.path,
										[`${i}`],
										['Beihilferelevanz_Selbsterklaerung'],
									)}
									formStruc={mapFormStruc(formStrucPruefAssistenz(), i)}
								/>

								{/* ===== Tab 5: Erklärung Beihilferelevanz — Auswahl ===== */}
								<Box sx={{ mt: '1rem' }}>
									<InhaltWrapper>
										<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
											Erklärung Beihilferelevanz
										</Typography>
										<Typography>
											Der jeweils zutreffende Sachverhalt in Bezug auf die Beihilferelevanz der beantragten Förderung des
											Vorhabens ist nachvollziehbar und transparent zu erläutern.
										</Typography>
										<Typography sx={{ mt: '1rem', fontWeight: 600 }}>Bitte eine Auswahl vornehmen:</Typography>

										<Typography sx={{ mt: '0.5rem' }}>
											Wir / ich erkläre/n, dass die auf beihilferelevante Angebote entfallende Zuwendung an die begünstigten
											Start-up- und Scale-up-Unternehmen weitergereicht werden durch die
										</Typography>

										<CreateForm
											formStruc={mapFormStruc(formStrucBeihilferelevanteAngebote(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateAuswahl(key, value, i)}
											readOnly={updatedReadOnly}
										/>

										<Typography sx={{ mt: '1rem' }}>Wir / ich erkläre/n, dass</Typography>

										<CreateForm
											formStruc={mapFormStruc(formStrucBeihilfebegriff(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateAuswahl(key, value, i)}
											readOnly={updatedReadOnly}
										/>
									</InhaltWrapper>
								</Box>

								{/* ===== Conditional: Umsetzung einer transparenten Umlage ===== */}
								{angeboteValue === SELECTION_UMLAGE && (
									<Box sx={{ mt: '1rem' }}>
										<InhaltWrapper>
											<Typography>
												Es ist transparent darzulegen, wie der Beihilfewert pro Angebot ermittelt wird und welcher Beihilfewert
												als Euro-Betrag sich bei Nutzung eines Angebots für das begünstigte Unternehmen ergibt.
											</Typography>
											<Typography sx={{ mt: '0.5rem' }}>
												Bitte fertigen Sie dazu eine prüfbare und transparente Berechnungstabelle an (Excel-Format).
											</Typography>
											<Typography sx={{ mt: '0.5rem' }}>
												Zur Ermittlung der Umlage (hier: „Beihilfewert" im Sinne einer De-minimis-Beihilfe) sind alle Ausgaben des
												Projektes (alle Personal- und Sachausgaben) auf die Tätigkeiten und Maßnahmen im Vorhaben zur Schaffung von
												Angeboten in einer transparenten Tabelle aufzuteilen.
											</Typography>

											<CreateTable
												formStruc={mapFormStruc(formStrucUmlageTable(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>

											<Typography sx={{ mt: '1rem', fontWeight: 600 }}>
												Ich verpflichte mich / Wir verpflichten uns,
											</Typography>
										</InhaltWrapper>

										<CreateForm
											formStruc={mapFormStruc(formStrucUmlageVerpflichtungen(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
											readOnly={updatedReadOnly}
										/>

										<InhaltWrapper>
											<Typography variant={'h6'} component={'p'} sx={{ fontWeight: 600, mt: '1rem' }}>
												Upload der Berechnungstabelle für das Umlagemodell
											</Typography>
											<CreateTable
												formStruc={mapFormStruc(formStrucUmlageExcelUpload(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
												columnsLabel={['Bezeichnung des Dokuments', 'Datei']}
											/>
										</InhaltWrapper>
									</Box>
								)}

								{/* ===== Conditional: Abrechnung des Angebots zu Marktpreisen ===== */}
								{angeboteValue === SELECTION_MARKTPREISEN && (
									<Box sx={{ mt: '1rem' }}>
										<InhaltWrapper>
											<Typography>
												Bitte erläutern Sie, wie der Marktpreis für das jeweilige Angebot ermittelt wird und welchen Betrag die
												begünstigte Start-up-/Scale-up-Unternehmen für die Nutzung der Angebote bezahlen müssen (ggf. nach
												Nutzergruppe aufschlüsseln).
											</Typography>

											<CreateTable
												formStruc={mapFormStruc(formStrucMarktpreisenTable(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>

											<Typography sx={{ mt: '1rem', fontWeight: 600 }}>
												Ich verpflichte mich / Wir verpflichten uns,
											</Typography>
										</InhaltWrapper>

										<CreateForm
											formStruc={mapFormStruc(formStrucMarktpreisenVerpflichtungen(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
											readOnly={updatedReadOnly}
										/>
									</Box>
								)}

								{/* ===== Conditional: Beihilfebegriff trifft nicht zu ===== */}
								{isTrue(beihilfebegriffValue) && (
									<Box sx={{ mt: '1rem' }}>
										<InhaltWrapper>
											<Typography>
												Bitte erläutern Sie, wie die Beihilfefreiheit für die geschaffenen Angebote eindeutig und nachvollziehbar
												begründet wird.
											</Typography>

											<CreateTable
												formStruc={mapFormStruc(formStrucBeihilfefreiheit(), i)}
												formValues={application}
												updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
												readOnly={updatedReadOnly}
											/>

											<Typography sx={{ mt: '1rem', fontWeight: 600 }}>
												Ich verpflichte mich / Wir verpflichten uns,
											</Typography>
										</InhaltWrapper>

										<CreateForm
											formStruc={mapFormStruc(formStrucBeihilfefreiheitVerpflichtung(), i)}
											formValues={application}
											updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
											readOnly={updatedReadOnly}
										/>
									</Box>
								)}

								<InhaltTrenner marginTop={'2rem'} />

								{/* ===== Download Section (all paths) ===== */}
								<Box sx={{ mt: '1rem' }}>
									<InhaltWrapper>
										<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
											Download der Beihilferelevanz-Selbsterklärung
										</Typography>
										<Typography>
											Bitte laden Sie hier Ihre Beihilferelevanz-Selbsterklärung zur Unterzeichnung herunter:
										</Typography>
										<Stack direction={'row'} spacing={2}>
											<Button
												variant='outlined'
												startIcon={<Download />}
												sx={{ maxWidth: 'fit-content' }}
												onClick={() => handleGenerateSelbstErklaerung()}
											>
												Beihilferelevanz-Selbsterklärung für {unternehmenName}
											</Button>
										</Stack>
										<BeihilferelevanzNachdemDownloadInformation application={application} />
									</InhaltWrapper>
								</Box>

								<BeihilferelevanzPostVersandInfo application={application} />
							</React.Fragment>
						);
					}
					return null;
				})}
			</>
		);
	}
	return <MUIAlert message={V_PARTNER_KEINE_ANGABEN_TEXT} />;
};

export default BeihilferelevanzErklaerungenGlobal;

const mapFormStruc = (formStruc: FormStrucItem[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				return {
					...value,
					path: addIndexesToPath(get(value, 'path', ''), [`${index}`], ['Beihilferelevanz_Selbsterklaerung']),
				};
			}),
		};
	});
	return result;
};

// ===== PrüfAssistenz =====
const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
			],
		},
	];
	return struc;
};

// ===== Umlage: Berechnungstabelle (repeatable component from Strapi) =====
const formStrucUmlageTable = () => {
	const umlageComp = P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Umlage_Berechnungstabelle;
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...umlageComp,
					type: 'componentTable',
					validation: [{ validateFunction: NonRequired }],
					tableLabel: 'Berechnungstabelle Umlagemodell',
					fieldProps: {
						Umlage_Bezeichnung_Des_Angebots: { label: 'Bezeichnung des Angebots' },
						Umlage_Beihilfewert_Euro: { label: 'Beihilfewert (€)', format: 'currency' },
						Umlage_Anzahl_Erwarteten_Unternehmen: { label: 'Anzahl erwarteter Unternehmen' },
					},
				} as any,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Umlage: Verpflichtungen (4 checkboxes) =====
const formStrucUmlageVerpflichtungen = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Zur_Einholung_Erklaerung,
					type: 'checkbox',
					label:
						'von allen Begünstigten VOR der Erteilung einer Teilnahmegenehmigung an einem Angebot, für die der Beihilfebegriff zutrifft, eine zur wirtschaftlichen Tätigkeit einzuholen und den zutreffenden Sachverhalt sorgfältig und ggf. gestützt durch eigene Recherche zu prüfen, so dass Zweifel und Ungereimtheiten ausgeräumt werden.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Erteilung_Teilnahmegenehmigung,
					type: 'checkbox',
					label:
						'von allen wirtschaftlich tätigen Begünstigten VOR der Erteilung einer Teilnahmegenehmigung an einem Angebot, für die der Beihilfebegriff zutrifft, De-minimis-Selbsterklärungen einzuholen und sorgfältig zu prüfen.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Lesbar_Vollstaendig_Und_Fristgerecht,
					type: 'checkbox',
					label: 'lesbare, vollständig ausgefüllte und geprüfte Kopien der beiden o.g. Erklärungen',
					tooltip: 'Die Frist zur Vorlage der Dokumente wird im Falle einer Bewilligung im jeweiligen Zuwendungsbescheid bestimmt.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Vorgesehener_Zuwendungszweck,
					type: 'checkbox',
					label:
						'die beantragte Zuwendung ausschließlich für den vorgesehenen Zuwendungszweck einzusetzen und keine Quersubventionierung anderer, nicht unmittelbar mit dem Zuwendungszweck zusammenhängender wirtschaftlicher Tätigkeiten zuzulassen. Eine entsprechende Bestätigung ist im Verwendungsnachweis vorzunehmen.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Umlage: Excel file upload =====
const formStrucUmlageExcelUpload = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: 't.t',
					type: 'label',
					label: 'Berechnungstabelle_Umlagemodell',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Berechnungstabelle_Umlagemodell,
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
					allowedFileNameKeywords: ['Berechnungstabelle']
				},
			],
		},
	];
	return struc;
};

// ===== Marktpreis: Berechnungstabelle (repeatable component from Strapi) =====
const formStrucMarktpreisenTable = () => {
	const marktpreisComp = P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Abrechnung_Zu_Marktpreisen;
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...marktpreisComp,
					type: 'componentTable',
					validation: [{ validateFunction: NonRequired }],
					tableLabel: 'Berechnungstabelle Marktpreise',
					fieldProps: {
						Marktpreisen_Bezeichnung_Des_Angebots: { label: 'Bezeichnung des Angebots' },
						Marktpreisen_Beihilfewert_Euro: { label: 'Marktpreis (€)', format: 'currency' },
						Marktpreisen_Anzahl_Erwarteten_Unternehmen: { label: 'Anzahl erwarteter Unternehmen' },
					},
				} as any,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Marktpreis: Verpflichtungen (2 checkboxes) =====
const formStrucMarktpreisenVerpflichtungen = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Weiterreichung_Der_Foerderung_Abrechnung,
					type: 'checkbox',
					label:
						'die Weiterreichung der Förderung durch Vorlage entsprechender Rechnungskopien und ggf. Zahlungsnachweise im Verwendungsnachweis vollständig und nachvollziehbar zu dokumentieren.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Beantragte_Zuwendung_Abrechnung,
					type: 'checkbox',
					label:
						'die beantragte Zuwendung ausschließlich für den vorgesehenen Zuwendungszweck einzusetzen und keine Quersubventionierung anderer, nicht unmittelbar mit dem Zuwendungszweck zusammenhängender wirtschaftlicher Tätigkeiten zuzulassen. Eine entsprechende Bestätigung ist im Verwendungsnachweis vorzunehmen.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Beihilfefreiheit: Richtext field from Strapi =====
const formStrucBeihilfefreiheit = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Erlaeuterung_nachvollziehbare_Begruendung,
					type: 'text',
					validation: [{ validateFunction: NonRequired }],
					label:
						'Bitte erläutern Sie, wie die Beihilfefreiheit für die geschaffenen Angebote eindeutig und nachvollziehbar begründet wird.',
					maxCharLength: 3000,
					minRows: 4,
				} as any,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Beihilfefreiheit: Verpflichtung (1 checkbox) =====
const formStrucBeihilfefreiheitVerpflichtung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Verpflichtung_Keine_Quersubventionierung,
					type: 'checkbox',
					label:
						'die beantragte Zuwendung ausschließlich für den vorgesehenen Zuwendungszweck einzusetzen und keine Quersubventionierung anderer, nicht unmittelbar mit dem Zuwendungszweck zusammenhängender wirtschaftlicher Tätigkeiten zuzulassen. Eine entsprechende Bestätigung ist im Verwendungsnachweis vorzunehmen.',
					validation: [{ validateFunction: NonRequired }], // TODO_BACKEND: Remove once backend persists components
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Erklärung Beihilferelevanz: Auswahl (Radio: Umlage / Marktpreisen) =====
const formStrucBeihilferelevanteAngebote = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Erklaerung_Beihilferelevante_Angebote,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					label: '',
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Erklärung Beihilferelevanz: Beihilfebegriff trifft nicht zu (Radio) =====
const formStrucBeihilfebegriff = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Beihilferelevanz_Selbsterklaerung.schema.attributes.Erklaerung_Beihilfebegriff,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					label: '',
					options: [{ value: true, label: 'ausschließlich Angebote geschaffen werden, auf die der Beihilfebegriff nicht zutrifft.' }],
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

// ===== Validation: Dynamic formStruc for tree validation =====
export const getFormStrucBeihilferelevanzErklaerungGlobal = (
	application: any,
	unternehmenId: number,
	positionId?: string,
) => {
	let formStrucValidation: FormStrucItem[] = [];

	get(application, P.Beihilferelevanz_Selbsterklaerung.path, []).forEach((ele: any, i: number) => {
		if (
			(get(ele, `${BEIHILFERELEVANZ_PATH.Unternehmen}.id`, -1) === unternehmenId &&
				!get(ele, BEIHILFERELEVANZ_PATH.PositionID, null)) ||
			get(ele, BEIHILFERELEVANZ_PATH.PositionID, '') === positionId
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(
				ele,
				BEIHILFERELEVANZ_PATH.Pruefung_Pruefstatus,
				BEIHILFERELEVANZ_PATH.Pruefung_Erledigt,
			);

			const formStrucPruefAssistenzMapped = mapFormStruc(formStrucPruefAssistenz(), i);
			const formStrucBeihilferelevanteAngeboteMapped = mapFormStruc(formStrucBeihilferelevanteAngebote(), i);
			const formStrucBeihilfebegriffMapped = mapFormStruc(formStrucBeihilfebegriff(), i);

			// Determine active selection for conditional validation
			const angeboteValue = get(ele, BEIHILFERELEVANZ_PATH.Erklaerung_Beihilferelevante_Angebote, '');
			const beihilfebegriffValue = get(ele, BEIHILFERELEVANZ_PATH.Erklaerung_Beihilfebegriff, false);

			let conditionalFormStruc: FormStrucItem[] = [];

			if (isTrue(beihilfebegriffValue)) {
				// Beihilfefreiheit path
				conditionalFormStruc = concat(
					mapFormStruc(formStrucBeihilfefreiheit(), i),
					mapFormStruc(formStrucBeihilfefreiheitVerpflichtung(), i),
				);
			} else if (angeboteValue === SELECTION_UMLAGE) {
				// Umlage path
				conditionalFormStruc = concat(
					mapFormStruc(formStrucUmlageTable(), i),
					mapFormStruc(formStrucUmlageVerpflichtungen(), i),
					mapFormStruc(formStrucUmlageExcelUpload(), i),
				);
			} else if (angeboteValue === SELECTION_MARKTPREISEN) {
				// Marktpreis path
				conditionalFormStruc = concat(
					mapFormStruc(formStrucMarktpreisenTable(), i),
					mapFormStruc(formStrucMarktpreisenVerpflichtungen(), i),
				);
			}

			if (isHinweisFieldRequired) {
				formStrucValidation = concat(
					formStrucPruefAssistenzMapped,
					formStrucBeihilferelevanteAngeboteMapped,
					formStrucBeihilfebegriffMapped,
					conditionalFormStruc,
				);
			} else {
				formStrucValidation = concat(formStrucBeihilferelevanteAngeboteMapped, formStrucBeihilfebegriffMapped, conditionalFormStruc);
			}
		}
	});

	return formStrucValidation;
};
